import React from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { Dropdown } from "../../../HelperFunctions/PayrollDropdown";
import { ITEM_TYPE_OPTIONS } from "../../../Enums/Enum";
import { payrollSetupStyles } from "../../../styles/payrollSetup.styles";

/**
 * PayrollBasicInfoSection Component
 *
 * Renders the primary identity fields for a Payroll Item:
 * 1. Corporation selector (disabled during edits or prefilled corporation context)
 * 2. Payroll Item Name input
 * 3. Item Type selector (Wages, Deductions, Employee Taxes, Employer Taxes)
 * 4. Conditional Wage Type and Pay Type selectors (only visible when Item Type is "Wages")
 */
const PayrollBasicInfoSection = ({
  values,
  handleChange,
  setFieldValue,
  handleBlur,
  corporations = [],
  wageTypes = [],
  payTypes = [],
  isEditMode = false,
  prefillData = null,
  enqueueSnackbar,
}) => {
  const isCorpDisabled = isEditMode || Boolean(prefillData?.corpId);
  const isItemTypeDisabled = isEditMode || Boolean(prefillData?.itemTypeNumber);
  const isWages = values.itemType === "wages";

  return (
    <Stack gap={2}>
      {/* Row 1: Corporation & Item Name */}
      <Stack direction="row" gap={5}>
        {/* Corporation Selector */}
        <Box minWidth={200}>
          <Dropdown
            label={
              <>
                Corporation <span style={{ color: "red" }}>*</span>
              </>
            }
            name="corporation"
            value={
              corporations.find(
                (o) =>
                  o.corpValue &&
                  String(o.corpValue).toLowerCase() ===
                  String(values.corporation?.corpValue || "").toLowerCase()
              ) ||
              values.corporation || {
                corpName: "--Select--",
                corpValue: "000000000000000000000000000000000000",
              }
            }
            options={corporations}
            getOptionLabel={(o) => o?.corpName || ""}
            isEqual={(o, v) =>
              String(o?.corpValue || "").toLowerCase() ===
              String(v?.corpValue || "").toLowerCase()
            }
            onChange={setFieldValue}
            disabled={isCorpDisabled}
            sx={payrollSetupStyles.getBasicInfoDropdownSx(isCorpDisabled)}
            inputProps={{
              style: { paddingLeft: 12, paddingRight: 12 },
            }}
          />
        </Box>

        {/* Item Name Input */}
        <Box minWidth={200}>
          <Typography
            color="text.secondary"
            fontSize={11}
            fontWeight={500}
            mb={0.5}
            title="Item Name"
          >
            <span title="Item Name">Item Name <span style={{ color: "red" }}>*</span></span>
          </Typography>

          <span title={values.itemName || undefined} style={{ width: "100%", display: "inline-block" }}>
            <TextField
              name="itemName"
              value={values.itemName || ""}
              title={values.itemName || undefined}
              inputProps={{
                title: values.itemName || undefined,
              }}
              onChange={(e) => {
                // Allow letters, numbers, spaces, forward/backward slashes, and safe payroll characters
                if (/^[a-zA-Z0-9_()\s\-!',*.\\/" ]*$/.test(e.target.value)) {
                  handleChange(e);
                } else if (enqueueSnackbar) {
                  enqueueSnackbar(
                    "Item name does not support special characters.",
                    { variant: "error" }
                  );
                }
              }}
              variant="filled"
              size="small"
              fullWidth
              sx={payrollSetupStyles.basicInfoTextField}
              onBlur={handleBlur}
            />
          </span>
        </Box>
      </Stack>

      {/* Row 2: Item Type & Conditional Wage / Pay Type */}
      <Stack direction="row" gap={5}>
        {/* Item Type Selector */}
        <Box minWidth={200}>
          <Dropdown
            label={
              <>
                Item Type <span style={{ color: "red" }}>*</span>
              </>
            }
            name="itemType"
            value={
              [
                { label: "--Select--", value: "" },
                ...ITEM_TYPE_OPTIONS,
              ].find((o) => o.value === values.itemType) || {
                label: "--Select--",
                value: "",
              }
            }
            options={[
              { label: "--Select--", value: "" },
              ...ITEM_TYPE_OPTIONS,
            ]}
            getOptionLabel={(o) => o?.label || ""}
            isEqual={(o, v) => o?.value === v?.value}
            onChange={(name, option) => {
              setFieldValue(name, option?.value || "");
              // Reset wage and pay types when item type changes away from wages
              setFieldValue("wageType", "");
              setFieldValue("payType", "");
            }}
            disabled={isItemTypeDisabled}
            sx={payrollSetupStyles.getBasicInfoDropdownSx(isItemTypeDisabled)}
            inputProps={{
              style: { paddingLeft: 12, paddingRight: 12 },
            }}
            onBlur={handleBlur}
          />
        </Box>

        {/* Conditional Wage Type and Pay Type Fields */}
        {isWages && (
          <Stack gap={5} direction="row">
            {/* Wage Type */}
            <Box minWidth={200}>
              <Dropdown
                label={
                  <>
                    Wage Type <span style={{ color: "red" }}>*</span>
                  </>
                }
                name="wageType"
                value={
                  [
                    {
                      Value: "--Select--",
                      ID: "",
                      SourceID: "",
                      SourceType: "",
                    },
                    ...wageTypes,
                  ].find((o) => o.ID === values.wageType) || {
                    Value: "--Select--",
                    ID: "",
                    SourceID: "",
                    SourceType: "",
                  }
                }
                options={[
                  {
                    Value: "--Select--",
                    ID: "",
                    SourceID: "",
                    SourceType: "",
                  },
                  ...wageTypes,
                ]}
                getOptionLabel={(o) => o?.Value || ""}
                isEqual={(o, v) => o?.ID === v?.ID}
                onChange={(name, option) => {
                  if (!option) return;
                  setFieldValue("wageType", option.ID);
                  setFieldValue("wageTypeName", option.ID ? option.Value : "");
                  setFieldValue("wageSourceID", option.SourceID || "");
                  setFieldValue("wageSourceType", option.SourceType || "");
                  setFieldValue("payType", "");
                }}
                sx={payrollSetupStyles.getBasicInfoDropdownSx(false)}
                inputProps={{
                  style: { paddingLeft: 12, paddingRight: 12 },
                }}
                onBlur={handleBlur}
              />
            </Box>

            {/* Pay Type */}
            <Box minWidth={200}>
              <Dropdown
                label={
                  <>
                    Pay Type <span style={{ color: "red" }}>*</span>
                  </>
                }
                name="payType"
                value={
                  [
                    {
                      Value: "--Select--",
                      ID: "",
                      SourceID: "",
                      SourceType: "",
                    },
                    ...payTypes,
                  ].find((o) => o.ID === values.payType) || {
                    Value: "--Select--",
                    ID: "",
                    SourceID: "",
                    SourceType: "",
                  }
                }
                options={[
                  {
                    Value: "--Select--",
                    ID: "",
                    SourceID: "",
                    SourceType: "",
                  },
                  ...payTypes,
                ]}
                getOptionLabel={(o) => o?.Value || ""}
                isEqual={(o, v) => o?.ID === v?.ID}
                onChange={(name, option) => {
                  if (!option) return;
                  setFieldValue("payType", option.ID);
                  setFieldValue("paySourceID", option.SourceID || "");
                  setFieldValue("paySourceType", option.SourceType || "");
                }}
                sx={payrollSetupStyles.getBasicInfoDropdownSx(false)}
                inputProps={{
                  style: { paddingLeft: 12, paddingRight: 12 },
                }}
                onBlur={handleBlur}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default React.memo(PayrollBasicInfoSection);
